import React, { useState, useEffect } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowRight, ScanFace, Eye, EyeOff } from "lucide-react";
import { formatApiError } from "@/lib/errors";
import { Capacitor } from "@capacitor/core";
import {
  isBiometricAvailable,
  hasStoredCredentials,
  saveCredentials,
  authenticateAndGetCredentials,
  isNativeIOS,
} from "@/lib/biometric";

const BG_IMG =
  "https://static.prod-images.emergentagent.com/jobs/e247cb53-0744-421d-959a-0fd9f151a4c3/images/d0cb9624f7c14d8ee7c41e96633e79adabb14a74605190266701bf86b5a70f27.png";

// Distributor access is invitation-only. Rep agencies share their per-agency
// register link via InviteCoworkerDialog ("/register/:agencyId"). There is
// intentionally no self-service "Request access" link on this login page.
const BIOMETRIC_ENROLLED_KEY = "byrd_biometric_enrolled";
const BIOMETRIC_DISMISSED_KEY = "byrd_biometric_dismissed_session";

export default function LoginPage() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Biometric state
  const [bioAvailable, setBioAvailable] = useState(false);
  const [bioType, setBioType] = useState(null); // 2 = FaceID, 1 = TouchID
  const [bioHasCreds, setBioHasCreds] = useState(false);
  const [showEnrollPrompt, setShowEnrollPrompt] = useState(false);
  const [pendingCreds, setPendingCreds] = useState(null);

  // On mount: detect Face ID availability and stored credentials.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isNativeIOS()) return;
      const avail = await isBiometricAvailable();
      if (cancelled) return;
      setBioAvailable(avail.available);
      setBioType(avail.type);
      if (avail.available) {
        const has = await hasStoredCredentials();
        if (cancelled) return;
        setBioHasCreds(has);
        // If the keychain has been wiped (e.g. on a fresh build / signing
        // change) but our "user already chose" flag is still set in
        // localStorage, clear it so the enroll modal re-appears after their
        // next successful password login.
        if (!has && localStorage.getItem(BIOMETRIC_ENROLLED_KEY) === "1") {
          localStorage.removeItem(BIOMETRIC_ENROLLED_KEY);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) return null;
  if (user) return <Navigate to="/" replace />;

  const bioLabel = bioType === 1 ? "Touch ID" : "Face ID";

  const finishLogin = () => navigate("/", { replace: true });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const trimmedEmail = email.trim();
    try {
      await login(trimmedEmail, password);
      // Offer to enroll biometric on first password login when:
      //   - native iOS,
      //   - biometric is available on the device,
      //   - the iOS keychain has no saved credentials, AND
      //   - the user hasn't dismissed the prompt in this session.
      //
      // If keychain is empty but the persistent "already enrolled" flag is
      // still set (e.g. keychain wiped by a signing/bundle change), clear
      // the flag so the prompt fires again.
      if (bioAvailable && !bioHasCreds) {
        if (localStorage.getItem(BIOMETRIC_ENROLLED_KEY) === "1") {
          localStorage.removeItem(BIOMETRIC_ENROLLED_KEY);
        }
        const dismissedThisSession =
          sessionStorage.getItem(BIOMETRIC_DISMISSED_KEY) === "1";
        if (!dismissedThisSession) {
          setPendingCreds({ email: trimmedEmail, password });
          setShowEnrollPrompt(true);
        } else {
          finishLogin();
        }
      } else {
        finishLogin();
      }
    } catch (err) {
      setError(formatApiError(err, "Login failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const onSignInWithBiometric = async () => {
    setError("");
    const creds = await authenticateAndGetCredentials();
    if (!creds) {
      // Cancelled or failed — silent, user can just type their password.
      return;
    }
    setSubmitting(true);
    try {
      await login(creds.username, creds.password);
      finishLogin();
    } catch (err) {
      setError(formatApiError(err, "Biometric sign-in failed. Please use your password."));
    } finally {
      setSubmitting(false);
    }
  };

  const onConfirmEnroll = async () => {
    if (pendingCreds) {
      const ok = await saveCredentials(pendingCreds.email, pendingCreds.password);
      if (ok) localStorage.setItem(BIOMETRIC_ENROLLED_KEY, "1");
    }
    setShowEnrollPrompt(false);
    setPendingCreds(null);
    finishLogin();
  };

  const onDeclineEnroll = () => {
    // Dismissed only for THIS session — next cold-launch will re-offer.
    sessionStorage.setItem(BIOMETRIC_DISMISSED_KEY, "1");
    setShowEnrollPrompt(false);
    setPendingCreds(null);
    finishLogin();
  };

  return (
    <div className="min-h-screen flex">
      {/* Left image panel */}
      <div className="hidden lg:flex relative w-1/2 bg-slate-900 grain-overlay overflow-hidden">
        <img
          src={BG_IMG}
          alt="Industrial warehouse with precise lighting"
          className="absolute inset-0 w-full h-full object-cover opacity-90"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-slate-950/80 via-slate-900/30 to-transparent" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center">
            <div className="bg-white px-3 py-1 rounded-sm">
              <img
                src="/byrd-logo.png"
                alt="My Rep Agency"
                className="h-24 w-auto object-contain"
              />
            </div>
          </div>

          <div className="max-w-md">
            <div className="text-[11px] uppercase tracking-widest text-amber-400 mb-3 font-mono-data">
              Lighting & Electrical · Manufacturer's Rep
            </div>
            <h1 className="text-4xl font-display font-bold tracking-tight leading-tight">
              The product database
              <br />
              behind every quote.
            </h1>
            <p className="mt-4 text-slate-300 max-w-sm text-sm leading-relaxed">
              Build, organize, and maintain the spec data your distributors
              rely on — manufacturers, product lines, and custom attributes,
              all in one place.
            </p>
          </div>

          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-mono-data">
            v1.0 · Public Beta
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center bg-white px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center mb-10">
            <img
              src="/byrd-logo.png"
              alt="My Rep Agency"
              className="h-20 w-auto object-contain"
            />
          </div>

          <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-3 font-mono-data">
            Sign in
          </div>
          <h2 className="text-3xl font-display font-bold tracking-tight text-slate-900">
            Welcome back.
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Sign in to your distributor or rep agency account to continue.
          </p>

          {bioAvailable && bioHasCreds && (
            <div className="mt-8">
              <Button
                type="button"
                onClick={onSignInWithBiometric}
                disabled={submitting}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm h-11 group"
                data-testid="login-biometric-btn"
              >
                <ScanFace className="w-4 h-4 mr-2" />
                Sign in with {bioLabel}
              </Button>
              <div className="my-5 flex items-center gap-3">
                <div className="flex-1 h-px bg-slate-200" />
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono-data">
                  or use password
                </span>
                <div className="flex-1 h-px bg-slate-200" />
              </div>
            </div>
          )}

          <form
            onSubmit={onSubmit}
            className={bioAvailable && bioHasCreds ? "space-y-5" : "mt-8 space-y-5"}
            data-testid="login-form"
          >
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-slate-700">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yourcompany.com"
                className="mt-1.5 rounded-sm"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-slate-700">
                Password
              </Label>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="rounded-sm pr-10"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute inset-y-0 right-0 px-3 flex items-center text-slate-400 hover:text-slate-700"
                  data-testid="login-password-toggle"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className="mt-1.5 text-right">
                <Link
                  to="/forgot-password"
                  className="text-xs text-muted-foreground hover:text-slate-900 underline-offset-2 hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
            </div>

            {error ? (
              <div
                className="text-sm text-destructive bg-red-50 border border-red-200 px-3 py-2 rounded-sm"
                data-testid="login-error"
              >
                {error}
              </div>
            ) : null}

            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm h-11 group"
              data-testid="login-submit-btn"
            >
              {submitting ? "Signing in…" : "Sign in"}
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
            </Button>
          </form>

          {/* Rep-agency lead-gen callout — shown on both web AND native so
              any manufacturer's rep agency discovering the app can reach out. */}
          <div
            className="mt-8 pt-6 border-t border-slate-200"
            data-testid="rep-agency-cta"
          >
            <div className="rounded-sm border border-amber-200 bg-amber-50/70 px-4 py-3 text-sm text-slate-800">
              <div className="text-[10px] uppercase tracking-widest text-amber-700 font-mono-data font-semibold mb-1">
                Rep agencies
              </div>
              <div className="leading-relaxed">
                Interested in listing your lines on this platform?{" "}
                <a
                  href="mailto:wayne@myrepagency.com?subject=Rep%20agency%20listing%20inquiry"
                  className="font-semibold text-amber-800 underline underline-offset-2 hover:text-amber-900 break-all"
                  data-testid="rep-agency-cta-email"
                >
                  wayne@myrepagency.com
                </a>
              </div>
            </div>
          </div>

          {!Capacitor.isNativePlatform() && (
            <div
              className="mt-8 pt-6 border-t border-slate-200"
              data-testid="store-badges-section"
            >
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono-data text-center mb-3">
                Get the mobile app
              </div>
              <div className="flex items-center justify-center gap-3">
                <a
                  href="https://apps.apple.com/us/app/my-rep-agency/id6772835376"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Download on the App Store"
                  data-testid="app-store-badge"
                  className="block transition-transform hover:scale-[1.03] active:scale-[0.98]"
                >
                  <img
                    src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us"
                    alt="Download on the App Store"
                    className="h-10 w-auto"
                    loading="lazy"
                  />
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.byrdrepagency.search"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Get it on Google Play"
                  data-testid="google-play-badge"
                  className="block transition-transform hover:scale-[1.03] active:scale-[0.98]"
                >
                  <img
                    src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                    alt="Get it on Google Play"
                    className="h-[58px] w-auto"
                    loading="lazy"
                  />
                </a>
              </div>
            </div>
          )}

          <div className="mt-10 text-[11px] uppercase tracking-widest text-muted-foreground font-mono-data flex items-center justify-between">
            <span>For lighting &amp; electrical distributors</span>
            <div className="flex items-center gap-4">
              <Link
                to="/support"
                className="hover:text-slate-900 underline-offset-2 hover:underline"
                data-testid="support-link"
              >
                Support
              </Link>
              <Link
                to="/privacy"
                className="hover:text-slate-900 underline-offset-2 hover:underline"
                data-testid="privacy-policy-link"
              >
                Privacy
              </Link>
            </div>
          </div>
        </div>
      </div>

      <AlertDialog open={showEnrollPrompt} onOpenChange={setShowEnrollPrompt}>
        <AlertDialogContent data-testid="biometric-enroll-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Enable {bioLabel} for faster sign-in?</AlertDialogTitle>
            <AlertDialogDescription>
              Your credentials will be stored securely in the iOS Keychain on
              this device. You can disable this anytime from your profile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={onDeclineEnroll} data-testid="biometric-enroll-decline">
              Not now
            </AlertDialogCancel>
            <AlertDialogAction onClick={onConfirmEnroll} data-testid="biometric-enroll-accept">
              Enable {bioLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
